/**
 * The address a magic-link redemption is being attempted FOR, kept for the
 * length of one hop so the retry does not start from an empty field.
 *
 * WHY IT EXISTS. `/auth/link` hands the token and the address to Logto and the
 * document is gone. When the far side fails in a way that reaches `/callback`
 * (`sign_in_session.not_found` is the common one -- the interaction cookie lives
 * in the browser that STARTED the flow, and a magic link routinely opens in
 * another) the visitor gets an error card whose only exit is "Sign in again",
 * pointing at the door. The door then asked them to type the address again, from
 * a screen that had just been told which address the link was for. The three
 * Logto-side rejections that bounce straight to `/auth?email=...` keep the
 * prefill; this route lost it.
 *
 * WHY SESSIONSTORAGE, and why the key is not in the URL. The hop is
 * `/auth/link` -> Logto -> `/callback` -> `/auth`, all in ONE tab (the handoff is
 * a full-page navigation, which is the same property `silentReauthSlot`
 * has relied on for far longer than this feature). So a per-tab key spans it
 * exactly, and dies with the tab rather than waiting for the next person on the
 * profile. An `?email=` param would reopen the leak the whole magic-link capture
 * exists to close: Amplitude's autocapture ships the full URL with its page-view
 * event, the address sits in session history, and it rides along in any outbound
 * Referer. `/callback` therefore passes it to the door as ROUTER STATE, which the
 * door already reads (`location.state.email`).
 *
 * WHO SPENDS IT, and it is NOT every arrival. `/callback` consumes it when it
 * builds the retry, and the door drops it on mount whatever route got the visitor
 * there -- reaching either means this flow is over, and an address left behind
 * would prefill a later, unrelated door visit in the same tab.
 *
 * A SUCCESSFUL redemption spends nothing, and that is worth being accurate about:
 * the visitor lands in the app, never touches `/callback`'s error card and never
 * goes near the door, so the address sits in this tab's sessionStorage until
 * something else clears it -- a later door visit, a sign-out sweep
 * (`storageCleanup.ts` lists the key), or the tab closing. That is the same
 * lifetime as every other per-tab auth value and it holds nothing but an address
 * the visitor has just signed in with; it is stated here because "spent on
 * arrival" read as a guarantee, and only the failure path actually spends it.
 *
 * It is deliberately NOT a fourth prefill source in the door:
 * `getAuthDoorParams()` -> router state -> `?email=` is the precedence, and adding
 * a stash the door reads for itself would resurrect an address after the flow it
 * belonged to ended.
 *
 * Registered in `storageCleanup.ts` twice, and both entries matter: in
 * `SESSION_STORAGE_KEYS` so a sign-out takes it, and in `AUTH_HANDOVER_KEYS` so
 * `clearOutgoingAccountStorage` -- which `/auth/link` runs on its three
 * account-switch gates, in the same click that writes this -- does not sweep away
 * the retry address of the sign-in it is clearing the way for.
 */
import { AUTH_FLOW_TTL_MS } from '@utils/authFlowTtl';
import { defineSessionSlot, type SessionSlot } from '@utils/sessionSlot';

/** Per-tab, and named like the other `meetball_*` sessionStorage keys. */
export const AUTH_LINK_EMAIL_KEY = 'meetball_auth_link_email';

/**
 * The address this redemption is for. `/auth/link`'s confirm handler writes it,
 * beside the other things that must be in place before the document is
 * replaced; `/callback` reads it when it builds the retry and then clears it,
 * and the door clears it on mount.
 *
 * An empty address is refused (a token-only link names nobody, and never reaches
 * the writer). A failed write is harmless here: a prefill is not worth taking a
 * sign-in down for, and the populations where `sessionStorage` itself throws (iOS
 * Safari with "Block All Cookies", hardened mail WebViews) are this feature's own.
 *
 * It expires with the rest of the flow (`AUTH_FLOW_TTL_MS`), so an hours-old
 * address cannot prefill a later, unrelated door visit.
 */
export const redemptionEmailSlot: SessionSlot<string> = defineSessionSlot({
  key: AUTH_LINK_EMAIL_KEY,
  ttlMs: AUTH_FLOW_TTL_MS,
  isValue: (value): value is string => typeof value === 'string' && value.length > 0,
});
